/* TPL Link Stories (2026-11-02)
   Instagram-highlight style viewer for the rings on the link-in-bio page
   (sections/tpl-link-chapters.liquid). No libraries.

   - A ring with a matching story ([data-lcs-story] key == ring [data-lcs-ring]) opens the
     viewer instead of jumping to its card. Rings without a story keep the #anchor jump.
   - Slides auto-advance (section setting, default 5 s) via a CSS animation on the current
     progress bar, so pausing is just animation-play-state.
   - Tap the left third for back, the rest for next. Press and hold pauses. Swipe down or
     the close button closes. Esc closes, arrow keys move. Reduced motion: no auto-advance.
   - End of a ring moves to the next ring; the viewer closes after the last ring.
   - Only the current slide and the next one load their images.
   - Seen rings get .is-seen (grey ring), stored in localStorage (try/catch). */
(function () {
  'use strict';

  var SEEN_KEY = 'tpl-lc-seen';
  var HOLD_MS = 220;
  var SWIPE_CLOSE_PX = 90;
  var LOAD_TIMEOUT_MS = 8000;

  function readSeen() {
    try {
      var v = JSON.parse(window.localStorage.getItem(SEEN_KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function writeSeen(list) {
    try {
      window.localStorage.setItem(SEEN_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage blocked: seen state just won't persist */
    }
  }

  function prefersReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isVisible(el) {
    if (!el.getClientRects().length) return false;
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  function Viewer(root, dlg) {
    this.root = root;
    this.dlg = dlg;
    this.stage = dlg.querySelector('[data-lcs-stage]');
    this.bars = dlg.querySelector('[data-lcs-bars]');
    this.av = dlg.querySelector('[data-lcs-av]');
    this.name = dlg.querySelector('[data-lcs-name]');
    this.live = dlg.querySelector('[data-lcs-live]');
    this.pauseBtn = dlg.querySelector('[data-lcs-pause]');
    this.closeBtn = dlg.querySelector('[data-lcs-close]');
    this.seconds = parseFloat(dlg.getAttribute('data-lcs-seconds')) || 5;
    this.stories = [];
    this.triggers = {};
    this.reasons = {};
    this.open_ = false;
    this.ri = 0;
    this.si = 0;
    this.seen = readSeen();
    this.inerted = [];
    this.suppressClick = false;
    this.setup();
  }

  Viewer.prototype.setup = function () {
    var self = this;
    Array.prototype.forEach.call(this.dlg.querySelectorAll('[data-lcs-story]'), function (el) {
      var slides = Array.prototype.slice.call(el.querySelectorAll('.tpl-lcs__slide'));
      if (!slides.length) {
        el.parentNode.removeChild(el);
        return;
      }
      self.stories.push({ key: el.getAttribute('data-lcs-story'), label: el.getAttribute('data-label'), el: el, slides: slides });
    });
    if (!this.stories.length) return;

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-lcs-ring]'), function (t) {
      var key = t.getAttribute('data-lcs-ring');
      var idx = self.indexOf(key);
      if (idx < 0) return;
      self.triggers[key] = t;
      t.setAttribute('aria-haspopup', 'dialog');
      if (self.seen.indexOf(key) > -1) t.classList.add('is-seen');
      t.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        self.open(idx);
      });
    });

    this.dlg.style.setProperty('--lcs-dur', this.seconds + 's');
    this.bindControls();
  };

  Viewer.prototype.indexOf = function (key) {
    for (var i = 0; i < this.stories.length; i++) if (this.stories[i].key === key) return i;
    return -1;
  };

  /* ---------- open / close ---------- */

  Viewer.prototype.open = function (idx) {
    if (this.open_) return;
    this.open_ = true;
    this.static_ = prefersReduced();
    this.reasons = {};
    if (this.dlg.parentNode !== document.body) {
      /* theme editor re-renders leave an older copy in <body>; drop it */
      var old = document.getElementById(this.dlg.id);
      if (old && old !== this.dlg && old.parentNode === document.body) document.body.removeChild(old);
      document.body.appendChild(this.dlg);
    }
    this.dlg.hidden = false;
    this.dlg.classList.toggle('is-static', this.static_);
    this.dlg.classList.add('is-opening');
    var dlg = this.dlg;
    window.setTimeout(function () {
      dlg.classList.remove('is-opening');
    }, 400);
    this.lockPage();
    this.setUserPause(false);
    this.show(idx, 0, false);
    this.closeBtn.focus({ preventScroll: true });
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('visibilitychange', this.onVis);
  };

  Viewer.prototype.close = function () {
    if (!this.open_) return;
    this.open_ = false;
    var cur = this.stories[this.ri];
    this.clearCurrent();
    this.dlg.hidden = true;
    this.dlg.classList.remove('is-paused', 'is-holding', 'is-dragging');
    this.stage.style.transform = '';
    this.dlg.style.backgroundColor = '';
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visibilitychange', this.onVis);
    this.unlockPage();
    var t = cur && this.triggers[cur.key];
    if (t) {
      t.focus({ preventScroll: true });
      if (t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  Viewer.prototype.lockPage = function () {
    document.documentElement.classList.add('tpl-lcs-lock');
    var ss = window.TPLSmoothScroll;
    if (ss && ss.active && ss.lenis && ss.lenis.stop) ss.lenis.stop();
    var self = this;
    this.inerted = [];
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === self.dlg || el.tagName === 'SCRIPT' || el.inert) return;
      el.inert = true;
      self.inerted.push(el);
    });
  };

  Viewer.prototype.unlockPage = function () {
    document.documentElement.classList.remove('tpl-lcs-lock');
    var ss = window.TPLSmoothScroll;
    if (ss && ss.active && ss.lenis && ss.lenis.start) ss.lenis.start();
    this.inerted.forEach(function (el) {
      el.inert = false;
    });
    this.inerted = [];
  };

  /* ---------- slides ---------- */

  Viewer.prototype.build = function (li) {
    if (li._lcsBuilt) return;
    li._lcsBuilt = true;
    var m = document.createElement('div');
    m.className = 'tpl-lcs__media';
    var bgSrc = li.getAttribute('data-bg');
    if (bgSrc) {
      var bg = document.createElement('img');
      bg.className = 'tpl-lcs__bg';
      bg.alt = '';
      bg.decoding = 'async';
      bg.src = bgSrc;
      m.appendChild(bg);
    }
    var src = li.getAttribute('data-src');
    if (src) {
      var im = document.createElement('img');
      im.className = 'tpl-lcs__img';
      im.alt = li.getAttribute('data-alt') || '';
      im.decoding = 'async';
      var w = li.getAttribute('data-w');
      var h = li.getAttribute('data-h');
      if (w && h) {
        im.width = parseInt(w, 10);
        im.height = parseInt(h, 10);
      }
      var srcset = li.getAttribute('data-srcset');
      if (srcset) {
        im.sizes = '(min-width: 750px) 480px, 100vw';
        im.srcset = srcset;
      }
      im.src = src;
      var mark = function () {
        im.classList.add('is-loaded');
      };
      if (im.complete && im.naturalWidth) mark();
      else {
        im.addEventListener('load', mark);
        im.addEventListener('error', mark);
      }
      li._lcsImg = im;
      m.appendChild(im);
    }
    li.insertBefore(m, li.firstChild);
  };

  Viewer.prototype.nextPos = function (ri, si) {
    var s = this.stories[ri];
    if (si + 1 < s.slides.length) return [ri, si + 1];
    if (ri + 1 < this.stories.length) return [ri + 1, 0];
    return null;
  };

  Viewer.prototype.clearCurrent = function () {
    var s = this.stories[this.ri];
    if (!s) return;
    s.el.classList.remove('is-active');
    var li = s.slides[this.si];
    if (li) li.classList.remove('is-active');
    if (this.loadTimer) window.clearTimeout(this.loadTimer);
    delete this.reasons.loading;
  };

  Viewer.prototype.show = function (ri, si, announce) {
    var self = this;
    var ringChanged = ri !== this.ri || !this.stories[ri].el.classList.contains('is-active');
    this.clearCurrent();
    this.ri = ri;
    this.si = si;
    var s = this.stories[ri];
    var li = s.slides[si];

    s.el.classList.add('is-active');
    this.build(li);
    li.classList.add('is-active');

    var next = this.nextPos(ri, si);
    if (next) this.build(this.stories[next[0]].slides[next[1]]);

    if (ringChanged) this.setHeader(s);
    this.markSeen(s.key);
    this.renderBars(s, si);

    var im = li._lcsImg;
    if (im && !(im.complete && im.naturalWidth)) {
      this.reasons.loading = true;
      var done = function () {
        im.removeEventListener('load', done);
        im.removeEventListener('error', done);
        if (self.stories[self.ri].slides[self.si] !== li) return;
        delete self.reasons.loading;
        self.applyPause();
      };
      im.addEventListener('load', done);
      im.addEventListener('error', done);
      this.loadTimer = window.setTimeout(done, LOAD_TIMEOUT_MS);
    }
    this.applyPause();

    if (announce || ringChanged) {
      var title = li.querySelector('.tpl-lcs__t, .tpl-lcs__big');
      this.live.textContent =
        s.label + ', ' + (si + 1) + ' of ' + s.slides.length + (title ? ': ' + title.textContent.trim() : '');
    }
  };

  Viewer.prototype.setHeader = function (s) {
    this.name.textContent = s.label;
    this.av.textContent = '';
    var t = this.triggers[s.key];
    var src = t && t.querySelector('img');
    if (src) {
      var img = document.createElement('img');
      img.alt = '';
      img.width = 32;
      img.height = 32;
      img.src = src.currentSrc || src.src;
      this.av.appendChild(img);
    }
  };

  Viewer.prototype.renderBars = function (s, si) {
    var html = '';
    for (var i = 0; i < s.slides.length; i++) {
      var cls = i < si ? ' is-done' : i === si ? ' is-current' : '';
      html += '<span class="tpl-lcs__bar' + cls + '"><i></i></span>';
    }
    this.bars.innerHTML = html;
  };

  Viewer.prototype.markSeen = function (key) {
    var t = this.triggers[key];
    if (t) t.classList.add('is-seen');
    if (this.seen.indexOf(key) > -1) return;
    this.seen = this.seen.concat([key]);
    writeSeen(this.seen);
  };

  Viewer.prototype.next = function (announce) {
    var n = this.nextPos(this.ri, this.si);
    if (n) this.show(n[0], n[1], announce);
    else this.close();
  };

  Viewer.prototype.prev = function (announce) {
    if (this.si > 0) this.show(this.ri, this.si - 1, announce);
    else if (this.ri > 0) this.show(this.ri - 1, 0, announce);
    else this.show(this.ri, 0, announce);
  };

  /* ---------- pause ---------- */

  Viewer.prototype.applyPause = function () {
    var paused = Object.keys(this.reasons).length > 0;
    this.dlg.classList.toggle('is-paused', paused);
  };

  Viewer.prototype.pause = function (why) {
    this.reasons[why] = true;
    this.applyPause();
  };

  Viewer.prototype.resume = function (why) {
    delete this.reasons[why];
    this.applyPause();
  };

  Viewer.prototype.setUserPause = function (on) {
    if (on) this.pause('user');
    else this.resume('user');
    this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.pauseBtn.setAttribute('aria-label', on ? 'Play' : 'Pause');
  };

  /* ---------- input ---------- */

  Viewer.prototype.bindControls = function () {
    var self = this;
    var prevBtn = this.dlg.querySelector('[data-lcs-prev]');
    var nextBtn = this.dlg.querySelector('[data-lcs-next]');

    this.onKey = function (e) {
      if (!self.open_) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.next(true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.prev(true);
      } else if (e.key === 'Tab') {
        self.trapTab(e);
      }
    };

    this.onVis = function () {
      if (document.hidden) self.pause('hidden');
      else self.resume('hidden');
    };

    this.bars.addEventListener('animationend', function (e) {
      if (!self.open_ || self.static_) return;
      var bar = e.target.parentNode;
      if (bar && bar.classList.contains('is-current')) self.next(false);
    });

    this.closeBtn.addEventListener('click', function () {
      self.close();
    });
    this.pauseBtn.addEventListener('click', function () {
      self.setUserPause(self.pauseBtn.getAttribute('aria-pressed') !== 'true');
    });

    prevBtn.addEventListener('click', function () {
      if (self.consumeSuppress()) return;
      self.prev(true);
    });
    nextBtn.addEventListener('click', function () {
      if (self.consumeSuppress()) return;
      self.next(true);
    });

    [prevBtn, nextBtn].forEach(function (b) {
      b.addEventListener('contextmenu', function (e) {
        e.preventDefault();
      });
    });

    this.bindGesture();
  };

  Viewer.prototype.consumeSuppress = function () {
    if (!this.suppressClick) return false;
    this.suppressClick = false;
    return true;
  };

  Viewer.prototype.bindGesture = function () {
    var self = this;
    var g = null;
    var stage = this.stage;

    stage.addEventListener('pointerdown', function (e) {
      if (!self.open_ || e.button > 0) return;
      if (e.target.closest('a, .tpl-lcs__btn')) return;
      g = { id: e.pointerId, x: e.clientX, y: e.clientY, held: false, drag: false };
      g.timer = window.setTimeout(function () {
        if (!g || g.drag) return;
        g.held = true;
        self.pause('hold');
        self.dlg.classList.add('is-holding');
      }, HOLD_MS);
    });

    stage.addEventListener('pointermove', function (e) {
      if (!g || e.pointerId !== g.id) return;
      var dx = e.clientX - g.x;
      var dy = e.clientY - g.y;
      if (!g.drag && dy > 12 && Math.abs(dy) > Math.abs(dx)) {
        g.drag = true;
        window.clearTimeout(g.timer);
        self.dlg.classList.remove('is-holding');
        self.dlg.classList.add('is-dragging');
        self.pause('drag');
        try {
          stage.setPointerCapture(e.pointerId);
        } catch (err) {
          /* capture unsupported: drag still tracks while over the stage */
        }
      }
      if (g.drag) {
        var d = Math.max(0, dy);
        stage.style.transform = 'translateY(' + d + 'px) scale(' + (1 - Math.min(d / 2400, 0.08)) + ')';
        self.dlg.style.backgroundColor = 'rgba(0,0,0,' + Math.max(0.25, 1 - d / 400) + ')';
      }
    });

    function end(e) {
      if (!g || e.pointerId !== g.id) return;
      window.clearTimeout(g.timer);
      var dy = e.clientY - g.y;
      var wasDrag = g.drag;
      var wasHeld = g.held;
      g = null;
      self.dlg.classList.remove('is-holding', 'is-dragging');
      self.resume('hold');
      self.resume('drag');
      if (wasDrag) {
        self.suppressClick = true;
        if (dy > SWIPE_CLOSE_PX && e.type !== 'pointercancel') {
          self.close();
          return;
        }
        stage.classList.add('is-settling');
        stage.style.transform = '';
        self.dlg.style.backgroundColor = '';
        window.setTimeout(function () {
          stage.classList.remove('is-settling');
        }, 300);
        return;
      }
      if (wasHeld) self.suppressClick = true;
      /* clear a stale flag if no click follows (e.g. pointer released off the tap zone) */
      window.setTimeout(function () {
        self.suppressClick = false;
      }, 400);
    }
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
  };

  Viewer.prototype.trapTab = function (e) {
    var items = Array.prototype.filter.call(
      this.dlg.querySelectorAll('button, a[href]'),
      isVisible
    );
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !this.dlg.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !this.dlg.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-lcs-root]'), function (root) {
      if (root._lcsViewer) return;
      var dlg = root.querySelector('[data-lcs]');
      if (dlg) root._lcsViewer = new Viewer(root, dlg);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
